import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SERVER_URL } from "../../constant";
import ButtonCompo from "../../component/ButtonCompo";
import AlertBox from "../../component/AlertBox";
import { CircularProgress, LinearProgress } from "@mui/material";
import NotFound from "../../assets/not-found.png";
import { useSelector } from "react-redux";

function LeaveApproval() {
  const isEmployee = useSelector(
    (state) => state.employee?.isEmployeeAuthenticated
  );
  const employeeData = useSelector((state) => state.employee?.employeeData);

  const navigate = useNavigate();

  //redirect to signin if not logged in
  useEffect(() => {
    if (isEmployee !== undefined && isEmployee === false) {
      navigate("/employee/signin");
    }
  }, [isEmployee]);

  //redirect to employee dashboard if permission is not given
  useEffect(() => {
    if (employeeData !== null && employeeData?.admin?.leaveApproval === false) {
      navigate("/employee/dashboard");
    }
  }, [employeeData]);

  /* ----------------------------------------------------------------------------------------------------- */
  /* ----------------------------------------Main Coding Part--------------------------------------------- */
  /* ----------------------------------------------------------------------------------------------------- */
  const { leaveId } = useParams();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [leaveData, setLeaveData] = useState({});
  const [somethingError, setSomethingError] = useState(false);
  const [reload, setReload] = useState(false);

  async function getLeave() {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/employee/leave/application/get/single/${leaveId}`,
        method: "get",
        withCredentials: true,
      });
      console.log(res.data?.data);
      setLeaveData(res.data?.data);
    } catch (error) {
      console.log(error);
      setSomethingError(true);
    } finally {
      setLoading(false);
    }
  }

  async function changeStatus(status) {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/leave/approve/${leaveId}/${status}`,
        method: "get",
        withCredentials: true,
      });
      setReload((prev) => !prev);
      setOpen(true);
      setSuccess(true);
      setMessage("Leave status changed");
    } catch (error) {
      console.log(error);
      setOpen(true);
      setSuccess(false);
      setMessage("Action failed");
    } finally {
      setLoading(false);
    }
  }

  async function revokeApplication() {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/leave/revoke/${leaveId}`,
        method: "get",
        withCredentials: true,
      });
      setReload((prev) => !prev);
      setOpen(true);
      setSuccess(true);
      setMessage("Leave has been revoked");
    } catch (error) {
      console.log(error);
      setOpen(true);
      setSuccess(false);
      setMessage("Revoke failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getLeave();
  }, [leaveId, reload]);

  if (somethingError) {
    return (
      <div className="mt-[5rem] flex flex-col justify-center items-center w-full">
        <img src={NotFound} />
        <p className="w-full text-center text-slate-500 font-medium text-[18px]">
          Leave Application not found
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-[90vh] flex justify-center items-center">
        <CircularProgress size={"60px"} />
      </div>
    );
  }

  return (
    <>
      <div className="w-full flex justify-center items-center">
        {success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        {!success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        <div className="w-[95vw] md:w-[30rem] p-5 border mt-5 rounded-[10px] shadow-md">
          <h1 className="text-center text-[19px] font-medium">
            Leave Application
          </h1>
          <div className="mt-7 w-full space-y-2">
            <section className="grid grid-cols-2">
              <p className="font-medium">Applicant</p>
              <p className="text-slate-700">{leaveData?.user?.fullName}</p>
            </section>
            <section className="grid grid-cols-2">
              <p className="font-medium">Role</p>
              <p className="text-slate-700">{leaveData?.user?.role}</p>
            </section>
            <section className="grid grid-cols-2">
              <p className="font-medium">Leave Type</p>
              <p className="text-slate-700">
                {leaveData?.leaveName?.length > 0
                  ? leaveData?.leaveName
                  : leaveData?.leaveType}
              </p>
            </section>
            {leaveData?.createdAt && (
              <section className="grid grid-cols-2">
                <p className="font-medium">Applied At</p>
                <p className="text-slate-700">
                  {new Date(leaveData?.createdAt).toLocaleDateString("en-IN")}
                </p>
              </section>
            )}
            <section className="grid grid-cols-2">
              <p className="font-medium">From</p>
              <p className="text-slate-700">{`${leaveData?.startDom}-${
                Number(leaveData?.startMonth) + 1
              }-${leaveData?.startYear}`}</p>
            </section>
            <section className="grid grid-cols-2">
              <p className="font-medium">To</p>
              <p className="text-slate-700">{`${leaveData?.endDom}-${
                Number(leaveData?.endMonth) + 1
              }-${leaveData?.endYear}`}</p>
            </section>
            <section className="grid grid-cols-2">
              <p className="font-medium">Total Days</p>
              <p className="text-slate-700">{leaveData?.totalDays}</p>
            </section>
            <section className="grid grid-cols-2">
              <p className="font-medium">Leave Option</p>
              <p className="text-slate-700">
                {leaveData?.leaveOption === "Full"
                  ? "Full Day"
                  : leaveData?.leaveOption === "FH"
                  ? "First Half"
                  : "Second Half"}
              </p>
            </section>
            <section className="grid grid-cols-2">
              <p className="font-medium">Description</p>
              <p className="text-slate-700">{leaveData?.description}</p>
            </section>
            <section className="grid grid-cols-2">
              <p className="font-medium">Status</p>
              <p className="text-slate-700">
                {leaveData?.isApproved === undefined ? (
                  <span className="text-[13px] px-3 py-1 bg-yellow-200/40 border-yellow-300 border text-yellow-700 font-medium rounded-full">
                    Pending
                  </span>
                ) : leaveData?.isApproved === true ? (
                  <span className="text-[13px] px-3 py-1 bg-green-200/40 border-green-300 border text-green-700 font-medium rounded-full">
                    Approved
                  </span>
                ) : leaveData?.isRevoked === true ? (
                  <span className="text-[13px] px-3 py-1 bg-red-200/40 border-red-300 border text-red-700 font-medium rounded-full">
                    Revoked
                  </span>
                ) : (
                  <span className="text-[13px] px-3 py-1 bg-red-200/40 border-red-300 border text-red-700 font-medium rounded-full">
                    Rejected
                  </span>
                )}
              </p>
            </section>
          </div>
          {leaveData?.isApproved === undefined ? (
            <div className="flex w-ful justify-center items-center gap-3 mt-7">
              <ButtonCompo
                variant={"contained"}
                onClick={() => changeStatus(true)}
                color={"success"}
              >
                Approve
              </ButtonCompo>
              <ButtonCompo
                variant={"contained"}
                onClick={() => changeStatus(false)}
                color={"error"}
              >
                Reject
              </ButtonCompo>
            </div>
          ) : leaveData?.isApproved === true ? (
            <div className="flex w-ful justify-center items-center gap-3 mt-7">
              <ButtonCompo
                variant={"contained"}
                onClick={revokeApplication}
                color={"error"}
              >
                Revoke
              </ButtonCompo>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
    </>
  );
}

export default LeaveApproval;
